import styled from '@emotion/styled';
import type { fabric } from 'fabric';
import type { FabricEditor } from 'modules/fabric-editor';
import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignHorizontalSpaceAround,
  AlignStartHorizontal,
  AlignStartVertical,
  AlignVerticalSpaceAround,
  Crosshair,
} from 'lucide-react';
import React from 'react';
import InspectorSection from '../shared/inspector-section';

interface AlignmentSectionProps {
  editor: FabricEditor;
  active: fabric.Object[];
}

const AlignmentSection: React.FC<AlignmentSectionProps> = ({ editor, active }) => {
  const a = editor.alignment;
  if (!a || active.length === 0) return null;

  const single = active.length === 1;
  const canDistribute = active.length >= 3;

  return (
    <InspectorSection title="Выравнивание">
      <Grid>
        {!single && (
          <>
            <IconButton type="button" title="Выровнять влево" onClick={() => a.alignLeft()}>
              <AlignStartVertical size={16} />
            </IconButton>
            <IconButton type="button" title="Центрировать по вертикали" onClick={() => a.centerOnCanvasX()}>
              <AlignCenterVertical size={16} />
            </IconButton>
            <IconButton type="button" title="Выровнять вправо" onClick={() => a.alignRight()}>
              <AlignEndVertical size={16} />
            </IconButton>
            <IconButton type="button" title="Выровнять сверху" onClick={() => a.alignTop()}>
              <AlignStartHorizontal size={16} />
            </IconButton>
            <IconButton type="button" title="Центрировать по горизонтали" onClick={() => a.centerOnCanvasY()}>
              <AlignCenterHorizontal size={16} />
            </IconButton>
            <IconButton type="button" title="Выровнять снизу" onClick={() => a.alignBottom()}>
              <AlignEndHorizontal size={16} />
            </IconButton>
          </>
        )}
        {single && (
          <>
            <IconButton type="button" title="Центрировать по вертикали" onClick={() => a.centerOnCanvasX(active[0])}>
              <AlignCenterVertical size={16} />
            </IconButton>
            <IconButton type="button" title="Центрировать по горизонтали" onClick={() => a.centerOnCanvasY(active[0])}>
              <AlignCenterHorizontal size={16} />
            </IconButton>
          </>
        )}
      </Grid>
      {!single && (
        <Grid>
          <IconButton type="button" title="Центрировать первый к остальным (X)" onClick={() => a.centerFirstToOthersX()}>
            <Crosshair size={16} color="#007AFF" />
          </IconButton>
          <IconButton type="button" title="Центрировать первый к остальным (Y)" onClick={() => a.centerFirstToOthersY()}>
            <Crosshair size={16} color="#FF9500" />
          </IconButton>
          <IconButton
            type="button"
            title="Распределить по горизонтали"
            disabled={!canDistribute}
            onClick={() => a.distributeHorizontally()}
          >
            <AlignHorizontalSpaceAround size={16} />
          </IconButton>
          <IconButton
            type="button"
            title="Распределить по вертикали"
            disabled={!canDistribute}
            onClick={() => a.distributeVertically()}
          >
            <AlignVerticalSpaceAround size={16} />
          </IconButton>
        </Grid>
      )}
    </InspectorSection>
  );
};

export default AlignmentSection;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 6px;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  color: #111827;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }

  &:disabled {
    color: #9ca3af;
    cursor: not-allowed;
    background: #f9fafb;
  }
`;
